/**
 * ログイン後の遷移先を安全な同一サイト相対パスに限定する。
 * `next` クエリは攻撃者が操作可能なため、外部URL・プロトコル相対・スキームを弾き、
 * 不正値は "/"（最初の画面の振り分け＝app/page.tsx）にフォールバックする（オープンリダイレクト対策）。
 *
 * ログイン画面と2段階認証の画面も行き先にしない。/login?next=/two-factor が残ったまま
 * ログインすると6桁の画面へ送り返され、ログインの輪になる（2026-10-16 報告）。
 */
const AUTH_FLOW_PATHS = ['/login', '/two-factor']

function isAuthFlowPath(rawNext: string): boolean {
  // ブラウザと同じ読み替え（/x/../login・/%2e/login → /login）をした後のパスで判定する
  const pathname = new URL(rawNext, 'http://local').pathname
  return AUTH_FLOW_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))
}

export function resolveSafeNextPath(rawNext: string | null): string {
  if (
    rawNext &&
    rawNext.startsWith('/') &&
    !rawNext.startsWith('//') &&
    !rawNext.includes('\\') &&
    // 制御文字（改行・CR・タブ等）を弾く。ブラウザが /\n/evil.com を
    // https://evil.com に正規化するオープンリダイレクト迂回を防止する。
    !/[\u0000-\u001f\u007f]/.test(rawNext) &&
    !isAuthFlowPath(rawNext)
  ) {
    return rawNext
  }
  return '/'
}

/**
 * パスワードが通った後に移る6桁の画面の URL。ログイン画面の next（安全化した値）を引き継ぐ。
 * 引き継がないと、2FA を通った人は共有リンクの ?project=xxx 等を失い既定の画面へ行く（2026-10-16・Codex SO）。
 *
 * @param search ログイン画面の URL の ?…（window.location.search）
 */
export function buildTwoFactorPageHref(search: string): string {
  const rawNext = new URLSearchParams(search).get('next')
  if (!rawNext) return '/two-factor'
  return `/two-factor?next=${encodeURIComponent(resolveSafeNextPath(rawNext))}`
}

/**
 * ログインの直後に1回通る口（AUTH-01・2026-10-30）。行き先はこの口（サーバー）が決める：
 * 2段階認証が必須の人は戻り先より先に登録の画面へ、ほかの人は戻り先へ。
 * パスワードのログインと Google のログインが通る（6桁のあとは登録済みの人だけなので通さない）。
 */
export const AFTER_LOGIN_PATH = '/api/security/two-factor/after-login'

/** ログインが通ったあとに移る URL。戻り先は安全化してから付ける。 */
export function buildAfterLoginHref(nextPath: string): string {
  return `${AFTER_LOGIN_PATH}?next=${encodeURIComponent(resolveSafeNextPath(nextPath))}`
}

/**
 * 入口の見張り（lib/middleware/middleware-impl.ts）が、開いた住所（パス＋?の後ろ）をページへ渡す見出し。
 * x-pathname はパスだけ（?kind=moushide を落とす）なので別に持つ。
 */
export const REQUEST_PATH_HEADER = 'x-request-path'

/**
 * ログインが切れていた時に送るログイン画面の URL。開いた住所を戻り先（?next=）に付ける。
 * 🔴 付けないと、入力フォームの URL から来た人もログイン後に最初の画面（進捗管理表）へ行く
 *    （2026-11-01 古西さん「入力フォームのURLからログインしたのにログイン後に進捗管理表が表示される」）。
 * 戻り先が無い・最初の画面・安全でない時は /login だけ。
 */
export function buildLoginHref(rawNext: string | null): string {
  const next = resolveSafeNextPath(rawNext)
  return next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`
}

/** 画面の側（ブラウザ）で、いま開いている住所を戻り先にしたログイン画面の URL。 */
export function buildLoginHrefFromHere(): string {
  return buildLoginHref(window.location.pathname + window.location.search)
}

/** ログイン画面・6桁の画面が URL の ?next= から受け取った戻り先（安全化した値）。 */
export function nextPathFromSearch(search: string): string {
  return resolveSafeNextPath(new URLSearchParams(search).get('next'))
}
